import { Alert, AlertTitle, Box, capitalize, Container, Snackbar } from '@mui/material';
import type { FC } from 'react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Illustration2 from '~/assets/svgs/illustration-2.svg';
import { OrdersBanner } from '~/components/organisms/OrdersBanner';
import { OrdersTable } from '~/components/organisms/OrdersTable';
import { FeatureFlagged } from '~/components/utilities/FeatureFlagged';
import { B2C_ORDERS_PATH } from '~/constants';
import { useB2COrders } from '~/hooks/useB2COrders';
import { useSnackbar } from '~/hooks/useSnackbar';
import { Logger } from '~/lib/logger';

const logger = new Logger('B2C Orders Route');

export const B2Corders: FC = () => {
  const { snackbar, updateSnackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const {
    ordersData,
    isDataLoading,
    isApproveLoading,
    isRejectLoading,
    page,
    setPage,
    totalItems,
    status,
    setStatus,
    searchTerm,
    setSearchTerm,
    setDebouncedSearchTerm,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    handleApproveItems,
    handleRejectItems,
    isError,
    error,
    requestSupplierOrdersExport,
    isExportLoading,
  } = useB2COrders();

  const handleCloseSnackbar = () => {
    closeSnackbar();
    setTimeout(() => updateSnackbar(''), 500); // Delay to allow the snackbar to close
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      showSnackbar('Please select a start and end date', 'error');
      return;
    }

    await requestSupplierOrdersExport(startDate, endDate, () => {
      showSnackbar('Export is in progress. You will recieve an email once it is ready.', 'success');
    });
  };

  useEffect(() => {
    if (isError && error) {
      showSnackbar(error, 'error');
      logger.error(error, error);
    }
  }, [error, isError, showSnackbar]);

  return (
    <>
      <Snackbar
        open={snackbar.isOpen && snackbar.message !== ''}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.type} sx={{ width: '100%' }}>
          {snackbar.type && <AlertTitle>{capitalize(snackbar.type)}</AlertTitle>}
          {snackbar.message}
        </Alert>
      </Snackbar>

      <OrdersBanner
        imageUrl={Illustration2}
        count={totalItems}
        isLoading={isDataLoading}
        title="Orders"
        subtitle="Alternaleaf & Circuit system orders."
      />

      <FeatureFlagged featureFlag="expose_legacy_orders">
        <Container data-testid="legacy-orders-alert">
          <Box mt={2}>
            <Alert variant="standard" severity="warning">
              <h3>You are currently viewing legacy orders (orders placed before 6th Nov requiring approvals)</h3>
              <Link to={B2C_ORDERS_PATH}>Return to new orders</Link>
            </Alert>
          </Box>
        </Container>
      </FeatureFlagged>

      <OrdersTable
        data={ordersData}
        isLoading={isDataLoading}
        page={page}
        setPage={setPage}
        totalItems={totalItems}
        status={status}
        setStatus={setStatus}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setDebouncedSearchTerm={setDebouncedSearchTerm}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        handleApproveItems={handleApproveItems}
        approveLoading={isApproveLoading}
        handleRejectItems={handleRejectItems}
        rejectLoading={isRejectLoading}
        handleExport={handleExport}
        isExportLoading={isExportLoading}
      />
    </>
  );
};
